/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __setFunctionName = (this && this.__setFunctionName) || function (f, name, prefix) {
    if (typeof name === "symbol") name = name.description ? "[".concat(name.description, "]") : "";
    return Object.defineProperty(f, "name", { configurable: true, value: prefix ? "".concat(prefix, " ", name) : name });
};
import { SignalArray } from "signal-utils/array";
import { signal } from "signal-utils";
export class GraphEditHistory {
    #controller;
    #history = new EditHistoryManager();
    constructor(controller) {
        this.#controller = controller;
    }
    add(graph, label, creator, timestamp) {
        this.#history.add(graph, label, creator, timestamp);
        this.#controller.onHistoryChanged?.(this.#historyIncludingPending);
    }
    addEdit(graph, checkpoint, label, version, creator) {
        this.#history.pause(label, checkpoint, version, creator);
        this.#history.add(graph, label, creator);
        this.#controller.onHistoryChanged?.(this.#historyIncludingPending);
    }
    revertTo(index) {
        const revision = this.#history.revertTo(index);
        this.#controller.setGraph(revision.graph);
        this.#controller.onHistoryChanged?.(this.#historyIncludingPending);
        return revision;
    }
    canUndo() {
        return this.#history.canGoBack();
    }
    canRedo() {
        return this.#history.canGoForth();
    }
    undo() {
        this.#history.resume(this.#controller.graph(), this.#controller.version());
        const graph = this.#history.back();
        if (graph) {
            this.#controller.setGraph(graph);
        }
        this.#controller.onHistoryChanged?.(this.#historyIncludingPending);
    }
    redo() {
        this.#history.resume(this.#controller.graph(), this.#controller.version());
        const graph = this.#history.forth();
        if (graph) {
            this.#controller.setGraph(graph);
        }
        this.#controller.onHistoryChanged?.(this.#historyIncludingPending);
    }
    jump(index) {
        this.#history.resume(this.#controller.graph(), this.#controller.version());
        const graph = this.#history.jump(index);
        if (graph) {
            this.#controller.setGraph(graph);
        }
        this.#controller.onHistoryChanged?.(this.#historyIncludingPending);
    }
    entries() {
        return this.#history.history;
    }
    index() {
        return this.#history.index();
    }
    get pending() {
        return this.#history.pending;
    }
    get #historyIncludingPending() {
        const { pending, history } = this.#history;
        return pending
            ? [...history.slice(0, this.#history.index() + 1), pending]
            : history.slice();
    }
}
let EditHistoryManager = (() => {
    let _private_index_decorators;
    let _private_index_initializers = [];
    let _private_index_extraInitializers = [];
    let _private_index_descriptor;
    let _pending_decorators;
    let _pending_initializers = [];
    let _pending_extraInitializers = [];
    return class EditHistoryManager {
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            _private_index_decorators = [signal];
            _pending_decorators = [signal];
            __esDecorate(this, _private_index_descriptor = { get: __setFunctionName(function () { return this.#index_accessor_storage; }, "#index", "get"), set: __setFunctionName(function (value) { this.#index_accessor_storage = value; }, "#index", "set") }, _private_index_decorators, { kind: "accessor", name: "#index", static: false, private: true, access: { has: obj => #index in obj, get: obj => obj.#index, set: (obj, value) => { obj.#index = value; } }, metadata: _metadata }, _private_index_initializers, _private_index_extraInitializers);
            __esDecorate(this, null, _pending_decorators, { kind: "accessor", name: "pending", static: false, private: false, access: { has: obj => "pending" in obj, get: obj => obj.pending, set: (obj, value) => { obj.pending = value; } }, metadata: _metadata }, _pending_initializers, _pending_extraInitializers);
            if (_metadata) Object.defineProperty(this, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        }
        history = new SignalArray();
        #index_accessor_storage = __runInitializers(this, _private_index_initializers, 0);
        get #index() { return _private_index_descriptor.get.call(this); }
        set #index(value) { return _private_index_descriptor.set.call(this, value); }
        #pending_accessor_storage = (__runInitializers(this, _private_index_extraInitializers), __runInitializers(this, _pending_initializers, void 0));
        get pending() { return this.#pending_accessor_storage; }
        set pending(value) { this.#pending_accessor_storage = value; }
        pauseLabel = (__runInitializers(this, _pending_extraInitializers), null);
        #version = 0;
        current() {
            const entry = this.history[this.#index];
            if (!entry)
                return null;
            return structuredClone(entry.graph);
        }
        index() {
            return this.#index;
        }
        revertTo(index) {
            this.history.splice(index + 1);
            this.#index = index;
            this.pending = undefined;
            this.pauseLabel = null;
            return this.history[index];
        }
        add(graph, label, creator, timestamp) {
            if (this.paused())
                return;
            // Chop off the history at #index.
            this.history.splice(this.#index + 1);
            // Insert new entry.
            this.history.push({
                graph: structuredClone(graph),
                label,
                timestamp: timestamp ?? Date.now(),
                creator: creator ?? { role: "unknown" },
            });
            // Point #index the new entry.
            this.#index = this.history.length - 1;
        }
        canGoBack() {
            return this.#index > 0;
        }
        canGoForth() {
            return this.#index < this.history.length - 1;
        }
        back() {
            return this.jump(this.#index - 1);
        }
        forth() {
            return this.jump(this.#index + 1);
        }
        jump(newIndex) {
            if (this.paused()) {
                return null;
            }
            if (newIndex >= 0 && newIndex < this.history.length) {
                this.#index = newIndex;
            }
            return this.current();
        }
        paused() {
            return this.pauseLabel !== null;
        }
        pause(label, graph, version, creator) {
            if (this.pauseLabel !== label) {
                this.resume(graph, version);
            }
            this.pauseLabel = label;
            this.pending = {
                graph,
                label,
                timestamp: Date.now(),
                creator: creator ?? { role: "unknown" },
            };
            this.#version = version;
        }
        resume(graph, version) {
            if (this.pauseLabel === null)
                return;
            const label = this.pauseLabel;
            this.pauseLabel = null;
            if (this.#version !== version) {
                this.add(graph, label, this.pending?.creator);
            }
            this.pending = undefined;
        }
    };
})();
export { EditHistoryManager };
//# sourceMappingURL=history.js.map